import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import {
  RegPlate, StatusText, StatusPill, Icon, Skeleton, EmptyState, SectionHeader,
  ListControls, useListPrefs, Field, type SortOption,
} from '../components/ui';
import { type Customer } from '../lib/api';
import { api, type Asset } from '../lib/api';
import { useLoad, useDebounced, useAction } from '../lib/hooks';
import { formatDate } from '../../../shared/schedule';
import './screens.css';

type Filter = 'all' | 'vehicle' | 'trailer' | 'attention';
type Sort = 'due' | 'due-last' | 'reg' | 'customer' | 'inspected' | 'odometer';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'vehicle', label: 'Vehicles' },
  { key: 'trailer', label: 'Trailers' },
  { key: 'attention', label: 'Needs attention' },
];

const SORTS: SortOption<Sort>[] = [
  { key: 'due', label: 'Most urgent first', hint: 'Soonest of any due date' },
  { key: 'due-last', label: 'Least urgent first' },
  { key: 'reg', label: 'Registration A-Z' },
  { key: 'customer', label: 'Customer A-Z' },
  { key: 'inspected', label: 'Recently inspected' },
  { key: 'odometer', label: 'Highest mileage' },
];

/**
 * Days until the soonest obligation of any kind. The server already computes
 * the worst status across inspection, MOT, tacho, plating, LOLER, tax and
 * insurance - sorting on the inspection date alone put a vehicle with its MOT
 * due in five days below one due in eight weeks.
 */
const urgency = (a: Asset) => a.status?.days ?? Number.POSITIVE_INFINITY;

const text = (v: string | null | undefined) => (v ?? '').toUpperCase();

/** Anything overdue or falling due inside this window is pulled to the top. */
const DUE_WINDOW_DAYS = 28;

export default function Fleet() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Arriving from the "Inspect" button: picking a vehicle starts a sheet.
  const startMode = params.get('start') === '1';

  const [query, setQuery] = useState('');
  const [prefs, setPrefs, resetPrefs] = useListPrefs('fleet', {
    filter: 'all' as Filter,
    sort: 'due' as Sort,
    customerId: '',
  });
  const { filter, sort, customerId } = prefs;
  const search = useDebounced(query, 250);
  const { busy, error: actionError, run } = useAction();

  const { data: customerData } = useLoad<{ customers: Customer[] }>(() => api.get('/customers'));

  const { data, loading, error, reload } = useLoad<{ assets: Asset[] }>(
    () => api.get(`/assets${search ? `?q=${encodeURIComponent(search)}` : ''}`),
    [search],
  );

  const assets = useMemo(() => {
    let list = data?.assets ?? [];

    if (filter === 'vehicle' || filter === 'trailer') list = list.filter((a) => a.kind === filter);
    else if (filter === 'attention') {
      list = list.filter((a) => a.status?.state === 'overdue' || a.status?.state === 'due');
    }
    if (customerId) list = list.filter((a) => a.customer_id === customerId);

    const sorted = [...list];
    switch (sort) {
      case 'due-last':
        sorted.sort((a, b) => urgency(b) - urgency(a));
        break;
      case 'reg':
        sorted.sort((a, b) =>
          text(a.reg_no ?? a.fleet_no).localeCompare(text(b.reg_no ?? b.fleet_no)),
        );
        break;
      case 'customer':
        sorted.sort(
          (a, b) =>
            text(a.customer_name).localeCompare(text(b.customer_name)) || urgency(a) - urgency(b),
        );
        break;
      case 'inspected':
        // Never inspected sorts last rather than first.
        sorted.sort((a, b) => (b.last_inspection_date ?? '').localeCompare(a.last_inspection_date ?? ''));
        break;
      case 'odometer':
        sorted.sort((a, b) => (b.odometer ?? -1) - (a.odometer ?? -1));
        break;
      default:
        sorted.sort((a, b) => urgency(a) - urgency(b));
    }
    return sorted;
  }, [data, filter, sort, customerId]);

  /*
   * When the list is ordered by urgency - the default - the vehicles needing
   * attention in the next four weeks are split into their own block. Scrolling
   * a fleet of eighty to find the three that are due is the thing this screen
   * exists to prevent. Any other sort order is left as one flat list, because
   * the grouping would fight the ordering the user asked for.
   */
  const groups = useMemo(() => {
    if (sort !== 'due') return null;
    const soon = assets.filter((a) => {
      const d = a.status?.days;
      return d != null && d <= DUE_WINDOW_DAYS;
    });
    if (soon.length === 0 || soon.length === assets.length) return null;
    return { soon, rest: assets.filter((a) => !soon.includes(a)) };
  }, [assets, sort]);

  const startInspection = (asset: Asset) =>
    run(async () => {
      try {
        const result = await api.post<{ inspection: { id: string } }>('/inspections', {
          assetId: asset.id,
        });
        navigate(`/inspections/${result.inspection.id}/run`);
      } catch (err: any) {
        // An unfinished sheet already exists - carry on with that one.
        if (err?.status === 409 && err?.data?.existingId) {
          navigate(`/inspections/${err.data.existingId}/run`);
          return;
        }
        throw err;
      }
    });

  const renderRow = (asset: Asset) =>
    startMode ? (
      <button
        key={asset.id}
        type="button"
        className="listrow"
        disabled={busy}
        onClick={() => startInspection(asset)}
      >
        <AssetRowBody asset={asset} />
      </button>
    ) : (
      <Link key={asset.id} to={`/fleet/${asset.id}`} className="listrow">
        <AssetRowBody asset={asset} />
      </Link>
    );

  return (
    <Screen
      onRefresh={reload}
      title={startMode ? 'Start an inspection' : 'Fleet'}
      subtitle={startMode ? 'Choose the vehicle or trailer' : undefined}
      back={startMode ? '/' : undefined}
      action={
        !startMode ? (
          <Link to="/fleet/new" className="btn btn-primary btn-sm">
            <Icon name="plus" size={17} />
            Add
          </Link>
        ) : undefined
      }
    >
      <div className="stack-4">
        <div className="searchbar">
          <Icon name="search" size={18} />
          <input
            className="input"
            type="search"
            placeholder="Registration, fleet no, VIN or customer"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>

        <ListControls<Filter, Sort>
          chips={FILTERS}
          chip={filter}
          onChip={(key) => setPrefs({ filter: key })}
          sortOptions={SORTS}
          sort={sort}
          onSort={(key) => setPrefs({ sort: key })}
          activeFilterCount={(filter !== 'all' ? 1 : 0) + (customerId ? 1 : 0) + (sort !== 'due' ? 1 : 0)}
          onClearFilters={resetPrefs}
          filters={
            <Field label="Customer">
              <select
                className="select"
                value={customerId}
                onChange={(e) => setPrefs({ customerId: e.target.value })}
              >
                <option value="">All customers</option>
                {(customerData?.customers ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          }
        />

        {actionError ? (
          <div className="notice notice-danger" role="alert">
            {actionError}
          </div>
        ) : null}
        {error ? (
          <div className="notice notice-danger" role="alert">
            {error}
          </div>
        ) : null}

        {loading && !data ? <Skeleton rows={5} /> : null}

        {data && assets.length === 0 ? (
          <EmptyState
            title={search ? 'Nothing matches that' : 'No vehicles yet'}
            body={
              search
                ? 'Try a registration, fleet number or customer name.'
                : 'Add your first vehicle or trailer to start recording inspections.'
            }
            action={
              !search ? (
                <Link to="/fleet/new" className="btn btn-primary">
                  Add a vehicle
                </Link>
              ) : undefined
            }
          />
        ) : null}

        {groups ? (
          <div className="stack-5">
            <section className="stack">
              <SectionHeader title={`Due within ${DUE_WINDOW_DAYS / 7} weeks`} count={groups.soon.length} />
              <div className="panel divide">{groups.soon.map(renderRow)}</div>
            </section>
            <section className="stack">
              <SectionHeader title="Rest of the fleet" count={groups.rest.length} />
              <div className="panel divide">{groups.rest.map(renderRow)}</div>
            </section>
          </div>
        ) : null}

        {!groups && assets.length > 0 && (
          <div className="panel divide">
            {assets.map((asset) =>
              startMode ? (
                <button
                  key={asset.id}
                  type="button"
                  className="listrow"
                  style={{ width: '100%', textAlign: 'left', border: 'none' }}
                  disabled={busy}
                  onClick={() => startInspection(asset)}
                >
                  <AssetRowBody asset={asset} />
                </button>
              ) : (
                <Link key={asset.id} to={`/fleet/${asset.id}`} className="listrow">
                  <AssetRowBody asset={asset} />
                </Link>
              ),
            )}
          </div>
        )}

        {data && assets.length > 0 && assets.length !== data.assets.length ? (
          <p className="tiny dim" style={{ textAlign: 'center' }}>
            {assets.length} of {data.assets.length} shown
          </p>
        ) : null}
      </div>
    </Screen>
  );
}

function AssetRowBody({ asset }: { asset: Asset }) {
  const flagged =
    Boolean(asset.status) && asset.status!.state !== 'ok' && asset.status!.state !== 'unknown';

  return (
    <>
      <div className="listrow-lead">
        <RegPlate reg={asset.reg_no} fleetNo={asset.fleet_no} size="sm" />
      </div>
      {/*
        The status sits on the second line rather than beside the title. On a
        375px screen a pill in the title row leaves about 110px for the name,
        which turns "Mercedes-Benz Actros 2545" into "Mercedes-B…".
      */}
      <div className="grow">
        <div className="listrow-title truncate">
          {[asset.make, asset.model].filter(Boolean).join(' ') || (asset.kind === 'trailer' ? 'Trailer' : 'Vehicle')}
        </div>
        <div className="listrow-sub">
          <span className="truncate">{asset.customer_name ?? 'No customer'}</span>
          {/*
            The pill already says "8 days overdue", so the absolute date would
            only be repeating it in less space. Show one or the other.
          */}
          {flagged && asset.status!.state === 'overdue' ? (
            <StatusPill state="overdue" size="sm">
              {asset.status!.label}
            </StatusPill>
          ) : flagged ? (
            <StatusText state={asset.status!.state}>{asset.status!.label}</StatusText>
          ) : asset.next_inspection_date ? (
            <>
              <span className="dot" />
              <span>PMI {formatDate(asset.next_inspection_date).replace(/^0/, '').replace(/ \d{4}$/, '')}</span>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
